import { McsisConstants } from '@/models/constants/McsisConstants';
import _Vue from 'vue';
import AuthService from '@/services/auth.service';
import { McsisPolicy } from '@/models/constants/McsisPolicy';
import TokenService from '@/services/token.service';
import ApiService from '@/services/api.service';
import { McsisClaimDTO } from '@/models/McsisClaimDTO';
import ApiError from '@/exceptions/api.error';
import { McsisPolicyDTO } from '@/models/McsisPolicyDTO';
import jwt_decode from 'jwt-decode';
import { McsisRole } from '@/models/constants/McsisRole';
import moment from 'moment';

export function AuthPlugin(Vue: typeof _Vue, options?: AuthPluginOptions): void {
  // do stuff with options
  Vue.prototype.$getAcceptanceDate = getAcceptanceDate;
  Vue.prototype.$isAuthorized = isAuthorized;
  Vue.prototype.$isInRole = isInRole;
  Vue.directive('ShowByPolicy', ShowByPolicy);
  Vue.directive('ShowByRole', ShowByRole);
  Vue.directive('HideByRole', HideByRole);
}

const decodeToken = (): any => {
  const token = TokenService.getToken();
  let decodedToken = '';
  if (token) {
    decodedToken = jwt_decode(token) as any;
  }

  return decodedToken;
};

const getAcceptanceDate = (): string|undefined => {
    const decodedToken = decodeToken();
    if (decodedToken) {
      const TermsAcceptanceDateKey = 'TermsAcceptanceDate';
      const termsAcceptanceDate = decodedToken[TermsAcceptanceDateKey] as string;
      if (termsAcceptanceDate === undefined) {
        return undefined;
      } else {
        const date = moment(termsAcceptanceDate).format(McsisConstants.SwissDateFormat);

        return date;
      }
    }
    return undefined;
};

const isAuthorized = (policy: McsisPolicy): Promise<boolean> => {
  return fetchPolicies().then((policies: any) => {
    const decodedToken = decodeToken();
    if (decodedToken) {
      const requiredClaims = policies.get(policy);
      if (requiredClaims === undefined) {
        throw new Error(
          'No information received from the API about policy: ' +
          McsisPolicy[policy],
        );
      }
      for (const claimCode of requiredClaims) {
        if (decodedToken[claimCode] === undefined) {
          return false;
        }
      }
      return true;
    }
    return false;
  });
};

const isInRole = (role: McsisRole): Promise<boolean> => {
  const decodedToken = decodeToken();
  let isinRole = false;
  if (decodedToken) {
    if (Array.isArray(decodedToken.role)) {
      isinRole = decodedToken.role.some((r: string) => r === McsisRole[role]);
    } else {
      isinRole = decodedToken.role === McsisRole[role];
    }
  }

  return Promise.resolve(isinRole);
};

const fetchPolicies = (): Promise<Map<McsisPolicy, string[]>> => {
  let policies = AuthService.getPolicies();
  if (policies && policies.size > 0) {
    return Promise.resolve(policies);
  }
  // data services cannot be used since they may depend on this service
  return ApiService.get(`${process.env.VUE_APP_PUBLIC_ROOT_API}/auth/policies`,
  { handlerSuccessEnabled: false, handlerErrorEnabled: false })
    .then((response: any) => {
      const res = response.data as McsisPolicyDTO[];

      policies = new Map<McsisPolicy, string[]>();
      res.forEach((val: any) => {
        const policy: McsisPolicy =
          McsisPolicy[val.mcsisPolicyCode as keyof typeof McsisPolicy];
        if (policy === undefined) {
          throw new Error(
            `A policy returned by the API is unknown to the front-end code: ${val.mcsisPolicyCode}`,
          );
        }
        const claims: string[] = val.mcsisClaims.map(
          (claim: McsisClaimDTO) => claim.mcsisClaimCode,
        );
        const parentClaims: string[] = val.mcsisParentPolicy
          ? val.mcsisParentPolicy.mcsisClaims.map(
            (claim: McsisClaimDTO) => claim.mcsisClaimCode,
          )
          : [];
        for (const claimCode of parentClaims) {
          if (claims.indexOf(claimCode) === -1) {
            claims.push(claimCode);
          }
        }

        policies.set(policy, claims);
      });
      AuthService.savePolicies(policies);
      return policies;
    })
    .catch((error: any) => {
      throw new ApiError(error.response.status, error.response.statusText);
    });
};

const ShowByPolicy = {
  bind(el: any, binding: any) {
    el.style.display = 'none';
    isAuthorized(McsisPolicy[binding.value as keyof typeof McsisPolicy]).then(
      (response: boolean) => {
        if (el && response) {
          el.style.display = 'inherit';
        } else {
          el.remove();
        }
      },
    );
  },
};

const ShowByRole = {
  bind(el: any, binding: any) {
    el.style.display = 'none';
    isInRole(McsisRole[binding.value as keyof typeof McsisRole]).then(
      (visible: boolean) => {
        // Hide in case we don't have a token
        const decodedToken = decodeToken();
        if (!decodedToken) {
          el.remove();
          return;
        }

        if (el && visible) {
          el.style.display = 'inherit';
        } else {
          el.remove();
        }
      },
    );
  },
};

const HideByRole = {
  bind(el: any, binding: any) {
    el.style.display = 'none';
    isInRole(McsisRole[binding.value as keyof typeof McsisRole]).then(
      (visible: boolean) => {
        // Hide in case we don't have a token
        const decodedToken = decodeToken();
        if (!decodedToken) {
          el.remove();
          return;
        }

        if (el && visible) {
          el.remove();
        } else {
          el.style.display = 'inherit';
        }
      },
    );
  },
};

export { isInRole };

export class AuthPluginOptions { }
